import printJS from 'print-js';

import './Print_.scss';
import { translate } from '@chaika/i18n/helper';
import { assertIsDefined } from '@utils/assertIsDefined';

interface PrintElementParams {
    contentId: string;
    pageMargin?: string;
}

interface PrintParams {
    contentId: string;
    scanStyles?: boolean;
    onLoadingStart?(): void;
    onLoadingEnd?(): void;
}

const PRINT_ROOT_CLASS = 'print_root';
const PRINT_FRAME_STYLE = 'position:fixed;left:-10000px;top:0;width:794px;height:1123px;border:0;';

const styles = `
    div {
        font-family:Roboto,sans-serif;
    }
`;

export class Print {
    /**
     * Печатает элемент в собственном iframe и ждёт готовности шрифтов перед вызовом печати:
     * print-js печатает сразу после load и шрифты не ждёт, поэтому снимок печати рендерится
     * системным шрифтом вместо шрифтов приложения.
     *
     * Поля страницы задаются здесь, а не в scss: в печатный документ подключены все стили
     * приложения, поэтому `@page` из общего бандла изменил бы поля во всех печатных формах.
     */
    public static async printElement({ contentId, pageMargin }: PrintElementParams): Promise<void> {
        const content = document.getElementById(contentId);

        if (!content) {
            throw new Error(`Print content #${contentId} is not rendered`);
        }

        const pageStyle = pageMargin ? `<style>@page{margin:${pageMargin};}</style>` : '';
        const frame = await loadPrintFrame(
            `<html><head>${getStyleSheetLinks()}${pageStyle}</head>` +
                `<body class="${PRINT_ROOT_CLASS}">${content.outerHTML}</body></html>`,
        );

        assertIsDefined(frame.contentDocument, 'Loaded print frame has a document');
        await frame.contentDocument.fonts.ready;

        assertIsDefined(frame.contentWindow, 'Loaded print frame has a window');
        frame.contentWindow.print();
        frame.remove();
    }

    public static printContent({ contentId: printable, scanStyles, onLoadingStart, onLoadingEnd }: PrintParams) {
        const cssHrefs = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]')).map(
            l => new URL(getStyleSheetHref(l), window.location.href).href,
        );

        printJS({
            onLoadingStart,
            onLoadingEnd,
            printable,
            type: 'html',
            css: [
                ...cssHrefs,
                'https://fonts.googleapis.com/css2?family=Roboto+Condensed:wght@300;400;700&family=Roboto:wght@100;300;400;500;700;900&display=swap',
            ],
            documentTitle: '',
            style: `${styles}.hide_on_print{display:none!important;}.for_print{display:block!important;}#${printable}{width:700px!important;}`,
            scanStyles: !!scanStyles,
        });
    }

    public static printHtmlStringInFrame(htmlString: string, fileName: string = 'document'): void {
        const iframe = document.createElement('iframe');
        const prevTitle = document.title;

        const printStyles = `
        <style>
            @page {
                margin-top: 0;
                padding-top: 10mm;
                @top-center { content: none; }
                @top-left   { content: none; }
                @top-left-corner   { content: none; }
                @top-right  { content: none; }
                @top-right-corner  { content: none; }
 
                @bottom-center { content: none; }
                @bottom-left { 
                    font-size: 10px;
                    opacity: 0.5;
                    content: "${fileName}";
                }
                @bottom-right {
                    font-size: 10px;
                    opacity: 0.5;
                    content: "${translate('page')} " counter(page) " ${translate('fromIz')} " counter(pages);
                }
            }
        </style>
    `;
        const htmlWithStyles = htmlString.includes('<head>')
            ? htmlString.replace('<head>', `<head>${printStyles}`)
            : `<html><head>${printStyles}</head>${htmlString}</html>`;

        iframe.id = 'print-iframe';
        iframe.title = fileName;
        iframe.style.position = 'absolute';
        iframe.style.top = '-10000px';
        iframe.style.left = '-10000px';
        iframe.srcdoc = htmlWithStyles;

        document.title = fileName;
        document.body.appendChild(iframe);

        iframe.onload = () => {
            const iframeWindow = iframe.contentWindow;
            if (iframeWindow) {
                iframeWindow.focus();
                iframeWindow.print();
            }

            setTimeout(() => {
                document.title = prevTitle;
                document.body.removeChild(iframe);
            }, 1000);
        };
    }

    public static printPdf(params: printJS.Configuration) {
        printJS({
            type: 'pdf',
            ...params,
        });
    }
}

function loadPrintFrame(html: string): Promise<HTMLIFrameElement> {
    return new Promise(resolve => {
        const frame = document.createElement('iframe');

        frame.setAttribute('aria-hidden', 'true');
        frame.style.cssText = PRINT_FRAME_STYLE;
        frame.onload = () => resolve(frame);
        frame.srcdoc = html;

        document.body.appendChild(frame);
    });
}

function getStyleSheetLinks(): string {
    return Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]'))
        .map(link => `<link rel="stylesheet" href="${new URL(getStyleSheetHref(link), window.location.href).href}">`)
        .join('');
}

function getStyleSheetHref(link: HTMLLinkElement): string {
    const href = link.getAttribute('href');
    assertIsDefined(href, 'Stylesheet links are selected by the href attribute');
    return href;
}
